import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Download, X, ChevronDown, Share2 } from "lucide-react";
import { QRCode } from "react-qrcode-logo";
import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { QRFormValues, formSchema, SocialPlatform } from "./qr-generator/schemas/qr-schemas";
import { QRFormFields } from "./qr-generator/components/QRFormFields";
import { ColorPicker as QRColorPicker } from "./qr-generator/components/ColorPicker";
import { getQRCodeValue } from "./qr-generator/utils/qr-generator";
import { getFavicon } from "@/lib/utils/favicon";
import { socialIcons, generateIconUrl } from "@/lib/utils/social-icons";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { translations } from "@/lib/i18n/translations";
import { toast } from "sonner";

interface QRCodeGeneratorProps {
  t: typeof translations[keyof typeof translations];
}

function getDefaultValues(type: QRFormValues["type"]): QRFormValues {
  switch (type) {
    case "social": return { type, platform: "x", username: "" };
    case "vcard": return { type, firstName: "", lastName: "", phone: "", email: "", organization: "", street: "", city: "", state: "", zip: "", country: "" };
    case "phone": return { type, phone: "" };
    case "sms": return { type, phone: "", message: "" };
    case "email": return { type, email: "", subject: "", body: "" };
    case "wifi": return { type, ssid: "", password: "", encryption: "WPA" };
    case "crypto": return { type, cryptoType: "bitcoin", address: "", amount: "", message: "" };
    case "geo": return { type, latitude: "", longitude: "" };
    case "event": return { type, summary: "", description: "", startDate: "", endDate: "" };
    case "text": return { type, text: "" };
    case "mecard": return { type, name: "", phone: "", email: "" };
    default: return { type: "url", url: "" };
  }
}

const readNumber = (params: URLSearchParams, key: string, fallback: number, min: number, max: number) => {
  const rawValue = params.get(key);
  if (rawValue === null) return fallback;
  const value = Number(rawValue);
  return Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
};

const readRadius = (params: URLSearchParams, key: string) => {
  const values = (params.get(key) || "").split(",").map(Number);
  return [0, 1, 2, 3].map((index) => Number.isFinite(values[index]) ? Math.min(50, Math.max(0, values[index])) : 0) as [number, number, number, number];
};

export function QRCodeGenerator({ t }: QRCodeGeneratorProps) {
  const qrContainerRef = useRef<HTMLDivElement>(null);
  // Get all URL parameters
  const params = new URLSearchParams(window.location.search);
  const qrTypes: QRFormValues["type"][] = ["url", "social", "vcard", "phone", "sms", "email", "wifi", "crypto", "geo", "event", "text", "mecard"];
  const requestedType = params.get("type") as QRFormValues["type"];
  const urlType = qrTypes.includes(requestedType) ? requestedType : "url";
  const urlSize = readNumber(params, "size", 512, 128, 2000);
  const urlFgColor = params.get("fg_color") || "#000000";
  const urlBgColor = params.get("bg_color") || "#FFFFFF";
  const urlLogoSize = readNumber(params, "logo_size", 10, 5, 20);
  const urlLogoUrl = params.get("logo_url") || "";
  const urlAutoFavicon = params.get("auto_favicon") === "true";
  const urlUseSocialIcon = params.get("use_social_icon") !== "false"; // default to true
  const urlUseCryptoIcon = params.get("use_crypto_icon") !== "false";
  const urlQrStyle = (["squares", "dots", "fluid"] as const).find((style) => style === params.get("qr_style")) || "squares";
  const urlEyeRadiusTopLeft = readRadius(params, "eye_radius_top_left");
  const urlEyeRadiusTopRight = readRadius(params, "eye_radius_top_right");
  const urlEyeRadiusBottomLeft = readRadius(params, "eye_radius_bottom_left");
  const urlLogoPadding = readNumber(params, "logo_padding", 5, 0, 20);
  const urlLogoPaddingStyle = params.get("logo_padding_style") === "circle" ? "circle" : "square";
  const urlEcLevel = (["L", "M", "Q", "H"] as const).find((level) => level === params.get("ec_level")) || "H";
  const urlQuietZone = readNumber(params, "quiet_zone", 20, 10, 80);

  // Initialize form with type-specific values
  const getInitialValues = () => {
    switch (urlType) {
      case "url":
        return {
          type: "url" as const,
          url: params.get("url") || "",
        };
      case "social": {
        const platform = params.get("platform");
        const validPlatform = platform && ["x", "instagram", "facebook", "linkedin", "github", "tiktok", "youtube", "twitch", "bluesky"].includes(platform) ? platform as SocialPlatform : "x";
        return {
          type: "social" as const,
          platform: validPlatform,
          username: params.get("username") || "",
        };
      }
      case "vcard":
        return {
          type: "vcard" as const,
          firstName: params.get("first_name") || "",
          lastName: params.get("last_name") || "",
          phone: params.get("phone") || "",
          email: params.get("email") || "",
          organization: params.get("organization") || "",
          street: params.get("street") || "",
          city: params.get("city") || "",
          state: params.get("state") || "",
          zip: params.get("zip") || "",
          country: params.get("country") || "",
        };
      case "phone":
        return {
          type: "phone" as const,
          phone: params.get("phone") || "",
        };
      case "sms":
        return {
          type: "sms" as const,
          phone: params.get("phone") || "",
          message: params.get("message") || "",
        };
      case "email":
        return {
          type: "email" as const,
          email: params.get("email") || "",
          subject: params.get("subject") || "",
          body: params.get("body") || "",
        };
      case "wifi":
        return {
          type: "wifi" as const,
          ssid: params.get("ssid") || "",
          password: params.get("password") || "",
          encryption: (params.get("encryption") || "WPA") as "WPA" | "WEP" | "nopass",
        };
      case "crypto":
        return {
          type: "crypto" as const,
          cryptoType: (params.get("crypto_type") || "bitcoin") as "bitcoin" | "ethereum",
          address: params.get("address") || "",
          amount: params.get("amount") || "",
          message: params.get("message") || "",
        };
      case "geo":
        return {
          type: "geo" as const,
          latitude: params.get("latitude") || "",
          longitude: params.get("longitude") || "",
        };
      case "event":
        return {
          type: "event" as const,
          summary: params.get("summary") || "",
          description: params.get("description") || "",
          startDate: params.get("start_date") || "",
          endDate: params.get("end_date") || "",
        };
      case "text":
        return {
          type: "text" as const,
          text: params.get("text") || "",
        };
      case "mecard":
        return {
          type: "mecard" as const,
          name: params.get("name") || "",
          phone: params.get("phone") || "",
          email: params.get("email") || "",
        };
      default:
        return {
          type: "url" as const,
          url: "",
        };
    }
  };

  const form = useForm<QRFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getInitialValues(),
  });

  const type = form.watch("type");
  const url = form.watch("url");
  const platform = form.watch("platform");
  const cryptoType = form.watch("cryptoType");
  const formValues = form.watch();
  const qrValue = getQRCodeValue(formValues);

  const [fgColor, setFgColor] = useState(urlFgColor);
  const [bgColor, setBgColor] = useState(urlBgColor);
  const [logoUrl, setLogoUrl] = useState(urlLogoUrl);
  const [isRawOpen, setIsRawOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [useAutoFavicon, setUseAutoFavicon] = useState(urlAutoFavicon);
  const [useSocialIcon, setUseSocialIcon] = useState(urlUseSocialIcon);
  const [useCryptoIcon, setUseCryptoIcon] = useState(urlUseCryptoIcon);
  const [qrSize, setQrSize] = useState(urlSize);
  const [logoSize, setLogoSize] = useState(urlLogoSize);
  const [qrStyle, setQrStyle] = useState<'squares' | 'dots' | 'fluid'>(urlQrStyle);

  // Eye radius state for each corner
  const [eyeRadiusTopLeft, setEyeRadiusTopLeft] = useState<[number, number, number, number]>(urlEyeRadiusTopLeft);
  const [eyeRadiusTopRight, setEyeRadiusTopRight] = useState<[number, number, number, number]>(urlEyeRadiusTopRight);
  const [eyeRadiusBottomLeft, setEyeRadiusBottomLeft] = useState<[number, number, number, number]>(urlEyeRadiusBottomLeft);

  // Eye color state for each corner
  const [eyeColorTopLeftOuter, setEyeColorTopLeftOuter] = useState(params.get("eye_color_top_left_outer") || '#000000');
  const [eyeColorTopLeftInner, setEyeColorTopLeftInner] = useState(params.get("eye_color_top_left_inner") || '#000000');
  const [eyeColorTopRightOuter, setEyeColorTopRightOuter] = useState(params.get("eye_color_top_right_outer") || '#000000');
  const [eyeColorTopRightInner, setEyeColorTopRightInner] = useState(params.get("eye_color_top_right_inner") || '#000000');
  const [eyeColorBottomLeftOuter, setEyeColorBottomLeftOuter] = useState(params.get("eye_color_bottom_left_outer") || '#000000');
  const [eyeColorBottomLeftInner, setEyeColorBottomLeftInner] = useState(params.get("eye_color_bottom_left_inner") || '#000000');

  const [logoPadding, setLogoPadding] = useState(urlLogoPadding);
  const [logoPaddingStyle, setLogoPaddingStyle] = useState<'square' | 'circle'>(urlLogoPaddingStyle);
  const [ecLevel, setEcLevel] = useState<'L' | 'M' | 'Q' | 'H'>(urlEcLevel);
  const [quietZone, setQuietZone] = useState(urlQuietZone);
  const isFormValid = formSchema.safeParse(formValues).success;
  const payloadSize = new TextEncoder().encode(qrValue).length;
  const isTooLarge = payloadSize > { L: 2900, M: 2300, Q: 1600, H: 1200 }[ecLevel];
  const previewValue = isFormValid && !isTooLarge ? qrValue : "QR Forge";

  const handleResetStyle = () => {
    setFgColor('#000000');
    setBgColor('#FFFFFF');
    setQrStyle('squares');
    setEyeRadiusTopLeft([0, 0, 0, 0]);
    setEyeRadiusTopRight([0, 0, 0, 0]);
    setEyeRadiusBottomLeft([0, 0, 0, 0]);
    setEyeColorTopLeftOuter('#000000');
    setEyeColorTopLeftInner('#000000');
    setEyeColorTopRightOuter('#000000');
    setEyeColorTopRightInner('#000000');
    setEyeColorBottomLeftOuter('#000000');
    setEyeColorBottomLeftInner('#000000');
    setLogoPadding(5);
    setLogoPaddingStyle('square');
    setEcLevel('H');
    setQuietZone(20);
  };

  const getShareUrl = () => {
    const newParams = new URLSearchParams();
    const language = new URLSearchParams(window.location.search).get("lang");
    if (language) newParams.set("lang", language);

    newParams.set("type", type);

    switch (type) {
      case "url":
        if ("url" in formValues && formValues.url) newParams.set("url", formValues.url);
        break;
      case "social":
        if ("platform" in formValues && formValues.platform) {
          newParams.set("platform", formValues.platform);
        }
        if ("username" in formValues && formValues.username) {
          newParams.set("username", formValues.username);
        }
        break;
      case "vcard":
        if ("firstName" in formValues && formValues.firstName) {
          newParams.set("first_name", formValues.firstName);
        }
        if ("lastName" in formValues && formValues.lastName) {
          newParams.set("last_name", formValues.lastName);
        }
        if ("phone" in formValues && formValues.phone) {
          newParams.set("phone", formValues.phone);
        }
        if ("email" in formValues && formValues.email) {
          newParams.set("email", formValues.email);
        }
        if ("organization" in formValues && formValues.organization) {
          newParams.set("organization", formValues.organization);
        }
        if ("street" in formValues && formValues.street) {
          newParams.set("street", formValues.street);
        }
        if ("city" in formValues && formValues.city) {
          newParams.set("city", formValues.city);
        }
        if ("state" in formValues && formValues.state) {
          newParams.set("state", formValues.state);
        }
        if ("zip" in formValues && formValues.zip) {
          newParams.set("zip", formValues.zip);
        }
        if ("country" in formValues && formValues.country) {
          newParams.set("country", formValues.country);
        }
        break;
      case "mecard":
        if ("name" in formValues && formValues.name) newParams.set("name", formValues.name);
        if ("phone" in formValues && formValues.phone) newParams.set("phone", formValues.phone);
        if ("email" in formValues && formValues.email) newParams.set("email", formValues.email);
        break;
      case "phone":
      case "sms":
        if ("phone" in formValues && formValues.phone) {
          newParams.set("phone", formValues.phone);
        }
        if ("message" in formValues && formValues.message) {
          newParams.set("message", formValues.message);
        }
        break;
      case "email":
        if ("email" in formValues && formValues.email) {
          newParams.set("email", formValues.email);
        }
        if ("subject" in formValues && formValues.subject) {
          newParams.set("subject", formValues.subject);
        }
        if ("body" in formValues && formValues.body) {
          newParams.set("body", formValues.body);
        }
        break;
      case "wifi":
        if ("ssid" in formValues && formValues.ssid) {
          newParams.set("ssid", formValues.ssid);
        }
        if ("password" in formValues && formValues.password) {
          newParams.set("password", formValues.password);
        }
        if ("encryption" in formValues && formValues.encryption) {
          newParams.set("encryption", formValues.encryption);
        }
        break;
      case "crypto":
        if ("cryptoType" in formValues && formValues.cryptoType) {
          newParams.set("crypto_type", formValues.cryptoType);
        }
        if ("address" in formValues && formValues.address) {
          newParams.set("address", formValues.address);
        }
        if ("amount" in formValues && formValues.amount) {
          newParams.set("amount", formValues.amount);
        }
        if ("message" in formValues && formValues.message) {
          newParams.set("message", formValues.message);
        }
        break;
      case "event":
        if ("summary" in formValues && formValues.summary) {
          newParams.set("summary", formValues.summary);
        }
        if ("description" in formValues && formValues.description) {
          newParams.set("description", formValues.description);
        }
        if ("startDate" in formValues && formValues.startDate) {
          newParams.set("start_date", formValues.startDate);
        }
        if ("endDate" in formValues && formValues.endDate) {
          newParams.set("end_date", formValues.endDate);
        }
        break;
      case "geo":
        if ("latitude" in formValues && formValues.latitude) {
          newParams.set("latitude", formValues.latitude);
        }
        if ("longitude" in formValues && formValues.longitude) {
          newParams.set("longitude", formValues.longitude);
        }
        break;
      case "text":
        if ("text" in formValues && formValues.text) {
          newParams.set("text", formValues.text);
        }
        break;
    }

    newParams.set("size", qrSize.toString());
    newParams.set("fg_color", fgColor);
    newParams.set("bg_color", bgColor);
    newParams.set("logo_size", logoSize.toString());
    if (logoUrl) newParams.set("logo_url", logoUrl);
    newParams.set("auto_favicon", useAutoFavicon.toString());
    newParams.set("use_social_icon", useSocialIcon.toString());
    newParams.set("use_crypto_icon", useCryptoIcon.toString());
    newParams.set("qr_style", qrStyle);
    newParams.set("eye_radius_top_left", eyeRadiusTopLeft.join(','));
    newParams.set("eye_radius_top_right", eyeRadiusTopRight.join(','));
    newParams.set("eye_radius_bottom_left", eyeRadiusBottomLeft.join(','));
    newParams.set("eye_color_top_left_outer", eyeColorTopLeftOuter);
    newParams.set("eye_color_top_left_inner", eyeColorTopLeftInner);
    newParams.set("eye_color_top_right_outer", eyeColorTopRightOuter);
    newParams.set("eye_color_top_right_inner", eyeColorTopRightInner);
    newParams.set("eye_color_bottom_left_outer", eyeColorBottomLeftOuter);
    newParams.set("eye_color_bottom_left_inner", eyeColorBottomLeftInner);
    newParams.set("logo_padding", logoPadding.toString());
    newParams.set("logo_padding_style", logoPaddingStyle);
    newParams.set("ec_level", ecLevel);
    newParams.set("quiet_zone", quietZone.toString());

    const shareUrl = new URL(window.location.href);
    shareUrl.search = newParams.toString();
    return shareUrl.toString();
  };

  const handleTypeChange = (value: QRFormValues["type"]) => {
    form.reset(getDefaultValues(value));
    setLogoUrl("");
    setUseAutoFavicon(false);
    setUseSocialIcon(value === "social");
    setUseCryptoIcon(value === "crypto");
  };

  // Effect to fetch favicon when URL changes and auto-favicon is enabled
  useEffect(() => {
    async function updateFavicon() {
      if (type === "url" && useAutoFavicon && url) {
        try {
          const favicon = await getFavicon(url);
          if (favicon) {
            setLogoUrl(favicon);
          }
        } catch (error) {
          console.error('Error fetching favicon:', error);
        }
      }
    }

    updateFavicon();
  }, [url, type, useAutoFavicon]);

  // Effect to update logo when social platform changes or foreground color changes
  useEffect(() => {
    if (type === "social" && useSocialIcon && platform && socialIcons[platform]) {
      // Remove the # from the color and ensure it's lowercase
      const color = fgColor.replace('#', '').toLowerCase();
      const iconUrl = generateIconUrl(platform, color);
      setLogoUrl(iconUrl);
    } else if (type === "crypto" && useCryptoIcon && cryptoType && socialIcons[cryptoType]) {
      // Remove the # from the color and ensure it's lowercase
      const color = fgColor.replace('#', '').toLowerCase();
      const iconUrl = generateIconUrl(cryptoType, color);
      setLogoUrl(iconUrl);
    } else if ((type === "social" && (!platform || !socialIcons[platform])) || 
               (type === "crypto" && (!cryptoType || !socialIcons[cryptoType]))) {
      // Clear the logo URL if there's no icon for this platform
      setLogoUrl('');
    }
  }, [platform, cryptoType, type, useSocialIcon, useCryptoIcon, fgColor]);

  // Clear logo when auto-favicon or social icon is disabled
  useEffect(() => {
    if ((!useAutoFavicon && type === "url") || 
        (!useSocialIcon && type === "social") ||
        (!useCryptoIcon && type === "crypto")) {
      setLogoUrl('');
    }
  }, [useAutoFavicon, useSocialIcon, useCryptoIcon, type]);

  const handleDownload = async () => {
    try {
      if (!isFormValid || isTooLarge) throw new Error("QR content is not ready to export");
      const canvas = qrContainerRef.current?.querySelector("canvas");
      if (!canvas) throw new Error("QR code canvas not found");

      const output = document.createElement("canvas");
      output.width = qrSize;
      output.height = qrSize;
      const context = output.getContext("2d");
      if (!context) throw new Error("Could not create download canvas");
      context.drawImage(canvas, 0, 0, qrSize, qrSize);

      const blob = await new Promise<Blob>((resolve, reject) => {
        output.toBlob((result) => result ? resolve(result) : reject(new Error("Could not create PNG")), "image/png");
      });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `qr-code-${type}-${qrSize}px.png`;
      link.click();
      URL.revokeObjectURL(downloadUrl);
      toast.success(t.preview.downloadPNG);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(`${t.preview.downloadPNG} failed`);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      toast.success(t.preview.urlCopied);
    } catch (error) {
      console.error("Share failed:", error);
      toast.error("Could not copy the share URL");
    }
  };

  const eyeControls = [
    { label: "Top left", radius: eyeRadiusTopLeft, setRadius: setEyeRadiusTopLeft, outer: eyeColorTopLeftOuter, setOuter: setEyeColorTopLeftOuter, inner: eyeColorTopLeftInner, setInner: setEyeColorTopLeftInner },
    { label: "Top right", radius: eyeRadiusTopRight, setRadius: setEyeRadiusTopRight, outer: eyeColorTopRightOuter, setOuter: setEyeColorTopRightOuter, inner: eyeColorTopRightInner, setInner: setEyeColorTopRightInner },
    { label: "Bottom left", radius: eyeRadiusBottomLeft, setRadius: setEyeRadiusBottomLeft, outer: eyeColorBottomLeftOuter, setOuter: setEyeColorBottomLeftOuter, inner: eyeColorBottomLeftInner, setInner: setEyeColorBottomLeftInner },
  ];

  return (
    <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.08fr)_minmax(380px,0.92fr)]">
      <Card className="order-2 overflow-hidden xl:order-1">
        <CardHeader className="border-b-2 bg-foreground text-background">
          <div className="mb-3 flex items-center justify-between font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-background/70">
            <span>01 / Configure</span>
            <span>{type}</span>
          </div>
          <CardTitle className="text-2xl uppercase tracking-[-0.04em]">{t.settings.title}</CardTitle>
          <CardDescription className="text-background/65">{t.settings.description}</CardDescription>
        </CardHeader>
        <CardContent className="px-4 py-5 sm:px-6 sm:py-7">
          <div className="space-y-7">
            <div className="space-y-2">
              <Label htmlFor="type" className="field-label">{t.settings.type}</Label>
              <Select
                onValueChange={handleTypeChange}
                value={type}
              >
                <SelectTrigger id="type" className="w-full">
                  <SelectValue placeholder={t.settings.typeSelect} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="url">{t.types.url}</SelectItem>
                  <SelectItem value="social">{t.types.social}</SelectItem>
                  <SelectItem value="vcard">{t.types.vcard}</SelectItem>
                  <SelectItem value="phone">{t.types.phone}</SelectItem>
                  <SelectItem value="sms">{t.types.sms}</SelectItem>
                  <SelectItem value="email">{t.types.email}</SelectItem>
                  <SelectItem value="wifi">{t.types.wifi}</SelectItem>
                  <SelectItem value="crypto">{t.types.crypto}</SelectItem>
                  <SelectItem value="geo">{t.types.geo}</SelectItem>
                  <SelectItem value="event">{t.types.event}</SelectItem>
                  <SelectItem value="text">{t.types.text}</SelectItem>
                  <SelectItem value="mecard">{t.types.mecard}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />

            <QRFormFields form={form} t={t} />

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <Label className="section-label">Colors & Style</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetStyle}
                >
                  Reset Style
                </Button>
              </div>
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <QRColorPicker
                    color={fgColor}
                    onChange={setFgColor}
                    label="Foreground Color"
                  />
                  <QRColorPicker
                    color={bgColor}
                    onChange={setBgColor}
                    label="Background Color"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="field-label">QR Style</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: "squares" as const, label: "Squares", example: "Hello" },
                      { id: "dots" as const, label: "Dots", example: "Hello" },
                      { id: "fluid" as const, label: "Rounded", example: "Hello" },
                    ].map((styleOption) => (
                      <button
                        key={styleOption.id}
                        type="button"
                        onClick={() => setQrStyle(styleOption.id)}
                        aria-pressed={qrStyle === styleOption.id}
                        className={`relative flex min-w-0 flex-col items-center border-2 p-2 transition-[background-color,color,transform] ${qrStyle === styleOption.id
                            ? "bg-primary text-primary-foreground"
                            : "bg-background hover:-translate-y-0.5 hover:bg-accent hover:text-accent-foreground"
                          }`}
                      >
                        <div className="mb-2 flex aspect-square w-full max-w-20 items-center justify-center overflow-hidden border bg-white p-1">
                          <QRCode
                            value={styleOption.example}
                            size={60}
                            qrStyle={styleOption.id}
                            fgColor={fgColor}
                            bgColor={bgColor}
                            removeQrCodeBehindLogo={true}
                            eyeRadius={[
                              [0, 0, 0, 0], // top/left eye
                              [0, 0, 0, 0], // top/right eye
                              [0, 0, 0, 0]  // bottom/left eye
                            ]}
                            style={{ width: '100%', height: '100%' }}
                          />
                        </div>
                        <span className="truncate font-mono text-[10px] font-bold uppercase tracking-wider sm:text-xs">{styleOption.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="logo" className="section-label">{t.settings.logo}</Label>
              {type === "url" && (
                <div className="flex items-center space-x-2 mb-2">
                  <Checkbox
                    id="auto-favicon"
                    checked={useAutoFavicon}
                    onCheckedChange={(checked: boolean) => setUseAutoFavicon(checked)}
                  />
                  <Label htmlFor="auto-favicon" className="cursor-pointer">
                    {t.settings.autoFavicon}
                  </Label>
                </div>
              )}
              {type === "social" && platform && socialIcons[platform] && (
                <div className="flex items-center space-x-2 mb-2">
                  <Checkbox
                    id="social-icon"
                    checked={useSocialIcon}
                    onCheckedChange={(checked: boolean) => setUseSocialIcon(checked)}
                  />
                  <Label htmlFor="social-icon" className="cursor-pointer">
                    {t.settings.useSocialIcon}
                  </Label>
                </div>
              )}
              {type === "crypto" && cryptoType && socialIcons[cryptoType] && (
                <div className="flex items-center space-x-2 mb-2">
                  <Checkbox
                    id="crypto-icon"
                    checked={useCryptoIcon}
                    onCheckedChange={(checked: boolean) => setUseCryptoIcon(checked)}
                  />
                  <Label htmlFor="crypto-icon" className="cursor-pointer">
                    {t.settings.useCryptoIcon}
                  </Label>
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  id="logo"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder={t.settings.logoPlaceholder}
                  disabled={(type === "url" && useAutoFavicon) || (type === "social" && useSocialIcon && platform && socialIcons[platform] !== undefined) || (type === "crypto" && useCryptoIcon && cryptoType && socialIcons[cryptoType] !== undefined)}
                />
                {logoUrl && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      setLogoUrl("");
                      if (type === "url") setUseAutoFavicon(false);
                      if (type === "social") setUseSocialIcon(false);
                      if (type === "crypto") setUseCryptoIcon(false);
                    }}
                    aria-label="Remove logo"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {(logoUrl || (type === "social" && useSocialIcon && platform && socialIcons[platform])) && (
                <div className="space-y-2 mt-4">
                  <div className="flex items-center justify-between">
                    <Label>{t.settings.logoSize}</Label>
                    <span className="measure">{logoSize}%</span>
                  </div>
                  <Slider
                    aria-label={t.settings.logoSize}
                    value={[logoSize]}
                    onValueChange={(value: number[]) => setLogoSize(value[0])}
                    min={5}
                    max={20}
                    step={1}
                    className="w-full"
                  />
                </div>
              )}

              <Collapsible
                open={isAdvancedOpen}
                onOpenChange={setIsAdvancedOpen}
                className="mt-6 border-t-2 pt-4"
              >
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex w-full items-center justify-between px-0 hover:bg-transparent hover:text-accent"
                  >
                    <span className="section-label">Advanced controls</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${isAdvancedOpen ? "rotate-180" : ""
                        }`}
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-6 pt-6">
                  <div className="space-y-3">
                    <Label className="field-label">Eye style</Label>
                    <div className="grid gap-3 lg:grid-cols-3">
                      {eyeControls.map((eye) => (
                        <div key={eye.label} className="space-y-4 border-2 bg-background p-3">
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-xs font-bold uppercase tracking-wider">{eye.label}</span>
                            <span className="measure">{eye.radius[0]}%</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <QRColorPicker color={eye.outer} onChange={eye.setOuter} label="Outer" />
                            <QRColorPicker color={eye.inner} onChange={eye.setInner} label="Inner" />
                          </div>
                          <Slider
                            aria-label={`${eye.label} eye radius`}
                            value={[eye.radius[0]]}
                            onValueChange={([radius]) => eye.setRadius([radius, radius, radius, radius])}
                            min={0}
                            max={50}
                            step={1}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-4 border-t-2 pt-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Logo padding</Label>
                        <span className="measure">{logoPadding}px</span>
                      </div>
                      <Slider
                        aria-label="Logo padding"
                        value={[logoPadding]}
                        onValueChange={(value: number[]) => setLogoPadding(value[0])}
                        min={0}
                        max={20}
                        step={1}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="logo-padding-style">Logo padding shape</Label>
                      <Select
                        value={logoPaddingStyle}
                        onValueChange={(value: 'square' | 'circle') => setLogoPaddingStyle(value)}
                      >
                        <SelectTrigger id="logo-padding-style" className="w-full">
                          <SelectValue placeholder="Select padding style" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="square">Square</SelectItem>
                          <SelectItem value="circle">Circle</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="error-correction">Error correction</Label>
                      <Select
                        value={ecLevel}
                        onValueChange={(value: 'L' | 'M' | 'Q' | 'H') => setEcLevel(value)}
                      >
                        <SelectTrigger id="error-correction" className="w-full">
                          <SelectValue placeholder="Select error correction level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="L">Low (7%)</SelectItem>
                          <SelectItem value="M">Medium (15%)</SelectItem>
                          <SelectItem value="Q">Quartile (25%)</SelectItem>
                          <SelectItem value="H">High (30%)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Quiet zone</Label>
                        <span className="measure">{quietZone}px</span>
                      </div>
                      <Slider
                        aria-label="Quiet zone"
                        value={[quietZone]}
                        onValueChange={(value: number[]) => setQuietZone(value[0])}
                        min={10}
                        max={80}
                        step={1}
                        className="w-full"
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="order-1 overflow-hidden bg-accent xl:order-2 xl:sticky xl:top-4">
        <CardHeader className="border-b-2">
          <div className="mb-3 flex items-center justify-between font-mono text-[11px] font-bold uppercase tracking-[0.2em]">
            <span>02 / Output</span>
            <span>Live</span>
          </div>
          <CardTitle className="text-2xl uppercase tracking-[-0.04em]">{t.preview.title}</CardTitle>
          <CardDescription className="text-foreground/65">{t.preview.description}</CardDescription>
        </CardHeader>
        <CardContent className="px-4 py-5 sm:px-6 sm:py-7">
          <div className="space-y-6">
            <div className="flex items-center justify-center border-2 bg-background p-3 sm:p-6">
              <div ref={qrContainerRef} className="w-full max-w-[540px] overflow-hidden">
                <QRCode
                  value={previewValue}
                  size={1000}
                  fgColor={fgColor}
                  bgColor={bgColor}
                  qrStyle={qrStyle}
                  eyeRadius={[
                    [eyeRadiusTopLeft[0], eyeRadiusTopLeft[1], eyeRadiusTopLeft[2], eyeRadiusTopLeft[3]], // top/left eye
                    [eyeRadiusTopRight[0], eyeRadiusTopRight[1], eyeRadiusTopRight[2], eyeRadiusTopRight[3]], // top/right eye
                    [eyeRadiusBottomLeft[0], eyeRadiusBottomLeft[1], eyeRadiusBottomLeft[2], eyeRadiusBottomLeft[3]] // bottom/left eye
                  ]}
                  eyeColor={[
                    { // top/left eye
                      outer: eyeColorTopLeftOuter,
                      inner: eyeColorTopLeftInner
                    },
                    { // top/right eye
                      outer: eyeColorTopRightOuter,
                      inner: eyeColorTopRightInner
                    },
                    { // bottom/left eye
                      outer: eyeColorBottomLeftOuter,
                      inner: eyeColorBottomLeftInner
                    }
                  ]}
                  logoImage={logoUrl}
                  logoWidth={Math.floor(1000 * (logoSize / 50))}
                  logoHeight={Math.floor(1000 * (logoSize / 50))}
                  logoOpacity={1}
                  removeQrCodeBehindLogo={true}
                  ecLevel={ecLevel}
                  quietZone={quietZone}
                  enableCORS={true}
                  logoPadding={logoPadding}
                  logoPaddingStyle={logoPaddingStyle}
                  style={{ width: '100%', height: 'auto' }}
                />
              </div>
            </div>

            {(!isFormValid || isTooLarge) && (
              <p role="status" className="border-2 bg-background px-3 py-2 font-mono text-xs font-bold uppercase tracking-wider">
                {isTooLarge ? "Content is too large for this correction level." : "Complete the required fields to generate your QR."}
              </p>
            )}

            <Collapsible
              open={isRawOpen}
              onOpenChange={setIsRawOpen}
              className="space-y-2"
            >
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex w-full items-center justify-between border-y-2 px-0 hover:bg-transparent"
                >
                  <span className="field-label">{t.preview.rawText}</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${isRawOpen ? "rotate-180" : ""
                      }`}
                  />
                  <span className="sr-only">Toggle raw QR code</span>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2">
                <div className="border-2 bg-background p-4">
                  <code className="block max-h-36 overflow-auto font-mono text-xs break-all">{qrValue}</code>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="field-label">{t.preview.qrSize}</Label>
                <span className="measure bg-foreground text-background">{qrSize}px</span>
              </div>
              <Slider
                aria-label={t.preview.qrSize}
                value={[qrSize]}
                onValueChange={(value: number[]) => setQrSize(value[0])}
                min={128}
                max={2000}
                step={32}
                className="w-full"
              />
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <Button className="min-h-12" onClick={handleDownload} disabled={!isFormValid || isTooLarge}>
                <Download className="h-4 w-4" />
                {t.preview.downloadPNG}
              </Button>
              <Button
                variant="outline"
                className="min-h-12 bg-background"
                onClick={handleShare}
              >
                <Share2 className="h-4 w-4" />
                {t.preview.share}
              </Button>
              <p className="font-mono text-[10px] uppercase tracking-wider text-foreground/65 sm:col-span-2">
                Share links include the QR content. Nothing is added to your URL until you press share.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
